'use client';

import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface SearchableSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  /** Extra strings matched by the search field (e.g. email when label is display name). */
  keywords?: string[];
  /** Muted second line under the label in the list (e.g. the member's email). */
  description?: string;
}

export interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /**
   * Scrollable list area max height. Keeps the dropdown from growing past the viewport.
   * @default min(280px, 45vh)
   */
  listMaxHeight?: string;
  /** When false, omits the search field (list still scrolls if long). @default true */
  showSearch?: boolean;
  /** `compact` — h-8 trigger aligned with HubTable hub chrome */
  size?: 'default' | 'compact';
}

const defaultListMax = 'min(280px, 45vh)';

/**
 * Single-select for filter bars and dense UIs: typeahead search + capped height scroll list.
 *
 * Prefer this over raw Radix Select (`components/ui/select`) when lists can be long (owners, stages, etc.).
 * Form-style fields can keep using Radix Select.
 *
 * Used across the filter bars (often via the `FilterSelect` alias); grep for either name
 * for the current consumers rather than trusting a list here, which went stale the first
 * time one of those toolbars was replaced.
 */
export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  contentClassName,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No matches.',
  listMaxHeight = defaultListMax,
  showSearch = true,
  size = 'default',
}: SearchableSelectProps) {
  const compact = size === 'compact';
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [contentWidth, setContentWidth] = React.useState<number | undefined>(undefined);

  const selected = options.find((o) => o.value === value);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
      return;
    }
    requestAnimationFrame(() => {
      const el = triggerRef.current;
      if (el) setContentWidth(el.offsetWidth);
    });
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'justify-between rounded-md border-slate-200 bg-white font-normal shadow-sm hover:border-slate-300',
            compact ? 'h-8 px-2.5 text-[13px]' : 'h-9 px-3',
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-left">
            {selected ? (
              <>
                {selected.icon}
                <span className="truncate font-medium text-slate-700">{selected.label}</span>
                {selected.badge}
              </>
            ) : (
              <span className="truncate text-slate-500">{placeholder}</span>
            )}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/*
        The trigger sets the FLOOR of the panel width, not its width. Pinning the
        two together truncated every option whose label is longer than the
        control that opens it — the Customers filter rendered "Exclude custo…"
        in a 170px trigger, and the same thing happens to any filter sized to
        its resting label. The panel grows to its content and stops at the
        viewport; labels inside still truncate at that cap.
      */}
      <PopoverContent
        align="start"
        className={cn('w-auto max-w-[min(26rem,calc(100vw-2rem))] p-0', contentClassName)}
        style={contentWidth ? { minWidth: contentWidth } : undefined}
      >
        <Command shouldFilter>
          {showSearch && (
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
            />
          )}
          <CommandList className={cn('overflow-y-auto overflow-x-hidden')} style={{ maxHeight: listMaxHeight }}>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label, ...(option.keywords ?? [])]}
                  onSelect={() => {
                    onValueChange(option.value);
                    setSearch('');
                    setOpen(false);
                  }}
                  className="py-2"
                >
                  <Check
                    className={cn('h-4 w-4 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')}
                  />
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    {option.icon}
                    {option.description ? (
                      <span className="flex min-w-0 flex-col leading-tight">
                        <span className="truncate">{option.label}</span>
                        <span className="truncate text-[11px] text-slate-500">{option.description}</span>
                      </span>
                    ) : (
                      <span className="truncate">{option.label}</span>
                    )}
                    {option.badge}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
